import { useState } from 'react'

type PermissionRequestType = 'camera' | 'camera-microphone' | 'microphone'
type DeniedType = 'camera' | 'microphone' | 'camera-microphone'

const usePermission = (type: PermissionRequestType = 'camera') => {
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [deniedType, setDeniedType] = useState<DeniedType | null>(null)

  const requestAccess = async (): Promise<boolean> => {
    try {
      if (type === 'microphone') {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({
            name: 'microphone' as PermissionName
          })
          if (result.state === 'denied') {
            setDeniedType('microphone')
            setPermissionDenied(true)
            return false
          }
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        })
        stream.getTracks().forEach((track) => track.stop())
        return true
      }

      if (type === 'camera') {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({
            name: 'camera' as PermissionName
          })
          if (result.state === 'denied') {
            setDeniedType('camera')
            setPermissionDenied(true)
            return false
          }
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true
        })
        stream.getTracks().forEach((track) => track.stop())
        return true
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        })
        stream.getTracks().forEach((track) => track.stop())
        return true
      } catch {
        let cameraDenied = false
        let micDenied = false

        if (navigator.permissions) {
          const [cameraResult, micResult] = await Promise.all([
            navigator.permissions.query({ name: 'camera' as PermissionName }),
            navigator.permissions.query({
              name: 'microphone' as PermissionName
            })
          ])
          cameraDenied = cameraResult.state === 'denied'
          micDenied = micResult.state === 'denied'
        }

        if (cameraDenied && micDenied) {
          setDeniedType('camera-microphone')
        } else if (micDenied) {
          setDeniedType('microphone')
        } else {
          setDeniedType('camera')
        }

        setPermissionDenied(true)
        return false
      }
    } catch {
      if (type === 'camera-microphone') {
        setDeniedType('camera-microphone')
      } else if (type === 'microphone') {
        setDeniedType('microphone')
      } else {
        setDeniedType('camera')
      }
      setPermissionDenied(true)
      return false
    }
  }

  const clearPermissionDenied = () => {
    setPermissionDenied(false)
    setDeniedType(null)
  }

  return { permissionDenied, deniedType, requestAccess, clearPermissionDenied }
}

export default usePermission
